import React, { useState,useRef, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useNavigate } from 'react-router-dom';
import { Alert } from '@mui/material';

import {
  AppBar, Toolbar, Typography, Container, CssBaseline, ThemeProvider,
  Box, Stepper, Step, StepLabel, TextField, Link, Paper, Button,
  CircularProgress, List, ListItem, ListItemIcon, ListItemText, Avatar,
  Divider, MenuItem, Checkbox, FormControlLabel, IconButton,
} from '@mui/material';
import TelegramIcon from '@mui/icons-material/Telegram';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import WcIcon from '@mui/icons-material/Wc';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import BadgeIcon from '@mui/icons-material/Badge';
import SchoolIcon from '@mui/icons-material/School';
import {
  AccountCircle as AccountCircleIcon,
  CalendarToday as CalendarTodayIcon,
  Phone as PhoneIcon,
  Fingerprint as FingerprintIcon,
  Home as HomeIcon,
 } from '@mui/icons-material';
import { createTheme } from '@mui/material/styles';
import logo from '../logo.png';
import './styles.css';
import Footer from '../pages/Footer';
import { v4 as uuidv4 } from 'uuid';
import sha256 from 'crypto-js/sha256';
import Base64 from 'crypto-js/enc-base64url';
import axios from 'axios';
import { faker } from '@faker-js/faker';
import { Card, CardContent } from '@mui/material';
import { Snackbar, Alert as MuiAlert } from '@mui/material';

 

const defaultTheme = createTheme({
  palette: {
    primary: {
      main: 'rgb(18,82,166)',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Times New Roman", Times, serif',
  },
});

const steps = [
  'Provide Username',
  'Verify Fayda ID',
  'Review Fayda Profile',
  'Update Preference',
  'Complete Payment and Registration',
];

const API_BASE = 'https://exam.ethernet.edu.et/api';
const EXAM_CENTERS_TIMEOUT_MS = 90000;
const EDUNEWS_VIDEO_URL = 'https://www.youtube.com/@edunews_et';

/**
 * Seat count shown in the UI:
 * - Multiple exam days: sum of free_seats for each day (program may appear on each day).
 * - Single exam day: that day's free_seats (same as summing one day).
 * - No exam_days: fallback to API available_seats.
 */
const getDisplayFreeSeats = (campus) => {
  const days = campus?.exam_days;
  if (!days || !Array.isArray(days) || days.length === 0) {
    return typeof campus?.available_seats === 'number' ? campus.available_seats : 0;
  }
  if (days.length === 1) {
    const fs = days[0].free_seats;
    return typeof fs === 'number' ? fs : (typeof campus?.available_seats === 'number' ? campus.available_seats : 0);
  }
  return days.reduce(
    (sum, d) => sum + (typeof d?.free_seats === 'number' ? d.free_seats : 0),
    0
  );
};

const campusHasSelectableSeats = (campus) => getDisplayFreeSeats(campus) > 0;

const formatCampusLabel = (campus) => {
  const name = campus.exam_center_campus || campus.campus_name || 'Campus';
  if (!campusHasSelectableSeats(campus)) {
    return `${name} (Full)`;
  }
  const seats = getDisplayFreeSeats(campus);
  return `${name} (${seats.toLocaleString()} seats left)`;
};

const formatCenterRegionSuffix = (region) => {
  if (!region || typeof region !== 'string') return '';
  const normalized = region.trim().toLowerCase();
  if (!normalized || normalized === 'na' || normalized === 'n/a' || normalized === '-') {
    return '';
  }
  return ` — ${region.trim()}`;
};

/** Supports nested department object or flat department_id from API */
const resolveDepartmentId = (profile) => {
  if (!profile) return null;
  const id =
    profile.department?.id ??
    profile.department_id ??
    profile.departmentId;
  if (id === null || id === undefined || id === '') return null;
  return id;
};

export default function SignIn() {
  // Generate unique session ID for this tab to prevent data conflicts
  // Check if we're coming back from Fayda callback - if so, try to recover session ID
  const [sessionId] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    
    // If we have a callback code, try to find existing session data
    if (code) {
      console.log('🔍 Detected Fayda callback, searching for session data...');
      
      // Method 1: Try to match by state parameter (most reliable)
      if (state) {
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && key.startsWith('reg_') && key.endsWith('_oauth_state')) {
            const storedState = sessionStorage.getItem(key);
            if (storedState === state) {
              // Extract session ID from key: reg_{sessionId}_oauth_state
              const sessionIdPart = key.replace('reg_', '').replace('_oauth_state', '');
              const codeVerifier = sessionStorage.getItem(`reg_${sessionIdPart}_fayda_code_verifier`);
              const username = sessionStorage.getItem(`reg_${sessionIdPart}_exit_username`);
              
              if (codeVerifier && username) {
                console.log('✅ Recovered session ID from state parameter:', sessionIdPart);
                return sessionIdPart;
              }
            }
          }
        }
      }
      
      // Method 2: Search for existing session data by looking for fayda_code_verifier
      // This is a fallback if state matching fails
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('reg_') && key.endsWith('_fayda_code_verifier')) {
          // Extract session ID from key pattern: reg_{sessionId}_fayda_code_verifier
          // UUIDs contain dashes, so we need to be careful with the replacement
          const sessionIdPart = key.replace(/^reg_/, '').replace(/_fayda_code_verifier$/, '');
          const codeVerifier = sessionStorage.getItem(key);
          const username = sessionStorage.getItem(`reg_${sessionIdPart}_exit_username`);
          
          if (codeVerifier && username) {
            console.log('✅ Recovered session ID from code verifier:', sessionIdPart);
            return sessionIdPart;
          }
        }
      }
      
      console.warn('⚠️ Could not find existing session data for callback');
      console.warn('⚠️ This may cause the callback to fail. User may need to restart.');
    }
    
    // Otherwise, generate new session ID
    const newSessionId = uuidv4();
    console.log('🆕 Generated new session ID:', newSessionId);
    return newSessionId;
  });

  // Helper functions for session-scoped storage
  const getSessionKey = (key) => `reg_${sessionId}_${key}`;
  const persistDepartmentSession = (profile) => {
    const deptId = resolveDepartmentId(profile);
    if (deptId != null) {
      setSessionItem('department_id', String(deptId));
    }
  };
  const setSessionItem = (key, value) => {
    try {
      sessionStorage.setItem(getSessionKey(key), value);
    } catch (e) {
      console.error('Failed to set session item:', e);
    }
  };
  const getSessionItem = (key) => {
    try {
      return sessionStorage.getItem(getSessionKey(key));
    } catch (e) {
      console.error('Failed to get session item:', e);
      return null;
    }
  };
  const removeSessionItem = (key) => {
    try {
      sessionStorage.removeItem(getSessionKey(key));
    } catch (e) {
      console.error('Failed to remove session item:', e);
    }
  };
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Only cleanup if this is the last tab (optional - can be removed if you want data to persist)
      // For now, we'll keep data until explicit cleanup or browser close
    };
  }, []);

  const [activeStep, setActiveStep] = useState(0);
  const [username, setUsername] = useState('');
  const [userInfo, setUserInfo] = useState({examcenter: ''});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [studentProfile, setStudentProfile] = useState(null);
  const [fetchingProfile, setFetchingProfile] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [faydaLoading, setFaydaLoading] = useState(false);
  const [examCentersMeta, setExamCentersMeta] = useState({
    exam_topic: '',
    cohort_year: '',
    exam_centers: [],
  });
  const [loadingCenters, setLoadingCenters] = useState(false);
  const [centersLoadError, setCentersLoadError] = useState('');
  const [selectedExamCenterId, setSelectedExamCenterId] = useState('');
  const [selectedCampusId, setSelectedCampusId] = useState('');
  const [examCenterSaveSuccess, setExamCenterSaveSuccess] = useState(false);
  const [examCenterSaveError, setExamCenterSaveError] = useState(null);
  const centersFetchAbortRef = useRef(null);
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const address = userInfo?.address || {};
  const profilePicture = userInfo?.picture || '';
  const [savingFayda, setSavingFayda] = useState(false);  
  const [registrationStatus, setRegistrationStatus] = useState(null); // null = loading
  const [registrationMessage, setRegistrationMessage] = useState('');
  const [enableTicketDownload, setEnableTicketDownload] = useState(false);
  const [enableProfileView, setEnableProfileView] = useState(false);
  const [registrationDeadline, setRegistrationDeadline] = useState('');
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [statusError, setStatusError] = useState(null); // Track errors fetching status

   const [stepLoading, setStepLoading] = useState(false);
   const [isSaving, setIsSaving] = useState(false);

   const [savingExamCenter, setSavingExamCenter] = useState(false);

  const selectedCenter = examCentersMeta.exam_centers.find(
    (c) => String(c.id) === String(selectedExamCenterId)
  );
  const selectedCampus = selectedCenter?.campuses?.find(
    (c) => String(c.eap_campus_id) === String(selectedCampusId)
  );
  const canSaveExamCenter =
    selectedCenter &&
    selectedCampus &&
    campusHasSelectableSeats(selectedCampus) &&
    !savingExamCenter;

  useEffect(() => {
    if (activeStep !== 3) return undefined;

    const controller = new AbortController();
    centersFetchAbortRef.current = controller;

    const loadCenters = async () => {
      setLoadingCenters(true);
      setCentersLoadError('');
      setExamCenterSaveError(null);

      try {
        let profile = studentProfile;
        let departmentId =
          resolveDepartmentId(profile) || getSessionItem('department_id');

        if (!departmentId) {
          const username = getSessionItem('exit_username');
          if (!username) {
            setCentersLoadError(
              'Session expired. Please start again from Step 1 and confirm your profile.'
            );
            return;
          }
          const recordRes = await axios.post(`${API_BASE}/get-record`, { username });
          profile = recordRes.data;
          setStudentProfile(profile);
          persistDepartmentSession(profile);
          departmentId = resolveDepartmentId(profile);
        }

        if (!departmentId) {
          setCentersLoadError(
            'Department information is missing from your record. Please go back to Step 1, confirm your profile, or contact support.'
          );
          return;
        }

        const res = await axios.get(`${API_BASE}/get-examcenters`, {
          params: { department_id: departmentId },
          signal: controller.signal,
          timeout: EXAM_CENTERS_TIMEOUT_MS,
        });
        const data = res.data || {};
        const centers = data.exam_centers || [];

        if (data.error && centers.length === 0) {
          setCentersLoadError(data.error);
        }

        setExamCentersMeta({
          exam_topic: data.exam_topic || '',
          cohort_year: data.cohort_year || '',
          exam_centers: centers,
        });

        const examCenterId = profile?.examcenter?.id ?? studentProfile?.examcenter?.id;
        if (examCenterId) {
          setSelectedExamCenterId(String(examCenterId));
        }
      } catch (err) {
        if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') {
          return;
        }
        console.error('Failed to fetch exam centers:', err);
        let message =
          'Could not load exam centers. Please check your connection and try again.';
        if (err.code === 'ECONNABORTED') {
          message =
            'Loading exam centers timed out. Please wait a moment and go Back, then try again.';
        } else if (err.response?.data?.error) {
          message = err.response.data.error;
        }
        setCentersLoadError(message);
        setExamCentersMeta({ exam_topic: '', cohort_year: '', exam_centers: [] });
      } finally {
        setLoadingCenters(false);
      }
    };

    loadCenters();

    return () => {
      controller.abort();
    };
  }, [activeStep, studentProfile]);

  useEffect(() => {
    if (activeStep === 3) {
      setExamCenterSaveSuccess(false);
    }
  }, [activeStep]);

useEffect(() => {
  const fetchStatus = async () => {
    setLoadingStatus(true);
    setStatusError(null); // Clear any previous errors
    try {
      const res = await axios.get('https://exam.ethernet.edu.et/api/registration-status');
      if (res.data) {
        const status = res.data.registration_status || res.data.status || 'Closed';
        console.log('Registration status from backend:', status);
        setRegistrationStatus(status);  
        setRegistrationMessage(res.data.registration_message || res.data.message || '');
        setEnableTicketDownload(res.data.enable_ticket_download || false);
        setEnableProfileView(res.data.enable_profileview || false);
        setRegistrationDeadline(res.data.registration_deadline || '');
      }
    } catch (error) {
      console.error('Failed to fetch registration status', error);
      // Don't set status to 'Closed' on error - keep it null and show error message
      let errorMessage = 'Failed to fetch registration status. Please check your internet connection and try again.';
      
      if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        errorMessage = 'Network connection error. Please check your internet connection and try again.';
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'Request timed out. Please check your internet connection and try again.';
      } else if (error.response) {
        // Server responded with error status
        errorMessage = `Unable to load registration status (${error.response.status}). Please try again later.`;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setStatusError(errorMessage);
      setRegistrationStatus(null); // Keep as null to indicate unknown status
      setEnableTicketDownload(false);
      setEnableProfileView(false);
      setRegistrationDeadline('');
    } finally {
      setLoadingStatus(false);
    }
  };

  fetchStatus();
}, []);

  
  
  
  useEffect(() => {
    
  
    const handleFaydaCallback = async () => {
      console.log('🟡 Fayda callback handler started');
      console.log('🔑 Current session ID:', sessionId);

      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const codeVerifier = getSessionItem('fayda_code_verifier');
      const savedUsername = getSessionItem('exit_username');
      const savedState = getSessionItem('oauth_state');
      
      console.log('📋 Callback data check:', { 
        hasCode: !!code, 
        hasState: !!state,
        hasCodeVerifier: !!codeVerifier, 
        hasUsername: !!savedUsername,
        hasSavedState: !!savedState,
        stateMatch: state === savedState,
        sessionId 
      });
  
      if (!code) {
        console.warn('❌ Missing authorization code — skipping callback');
        return;
      }
      
      if (!codeVerifier) {
        console.error('❌ Missing code verifier — cannot complete OAuth flow');
        setSnackbar({ 
          open: true, 
          message: 'Session expired. Please start the registration process again from Step 1.', 
          severity: 'error' 
        });
        setActiveStep(0);
        return;
      }
      
      // Validate state parameter for security (CSRF protection)
      if (state && savedState && state !== savedState) {
        console.error('❌ State parameter mismatch — possible CSRF attack');
        setSnackbar({ 
          open: true, 
          message: 'Security validation failed. Please start again.', 
          severity: 'error' 
        });
        setActiveStep(0);
        return;
      }

      if (!savedUsername) {
        console.error('❌ Username not found — going back to Step 1');
        setSnackbar({ 
          open: true, 
          message: 'Username missing. Please start the registration process again from Step 1.', 
          severity: 'error' 
        });
        setActiveStep(0);
        return;
      }
      
      console.log('✅ All callback data validated, proceeding with token exchange...');
  
      setLoading(true);
  
      try {
        
        // Step 2: Fayda token exchange
        const faydaRes = await axios.get('https://exam.ethernet.edu.et/api/auth/callback', {
          params: { code, code_verifier: codeVerifier },
          timeout: 25000,
        });
        console.log('✅ Fayda profile fetched:', faydaRes.data);
        setUserInfo(faydaRes.data.user || {});

        try {
          const recordRes = await axios.post(`${API_BASE}/get-record`, {
            username: savedUsername,
          });
          setStudentProfile(recordRes.data);
          persistDepartmentSession(recordRes.data);
        } catch (recordErr) {
          console.warn('Could not reload student record after Fayda:', recordErr);
        }

        setActiveStep(2);
  
        setSnackbar({
          open: true,
          message: 'Fayda profile verified successfully!',
          severity: 'success',
        });
      } catch (err) {
        console.error('❌ Fayda callback error:', err);
        setError('Verification failed: ' + (err.message || 'Unknown error'));
        setSnackbar({
          open: true,
          message: err.message || 'Fayda verification failed. Try again.',
          severity: 'error',
        });
        setActiveStep(0);
      } finally {
        setLoading(false);
      }
    };
  
    const delay = setTimeout(() => {
      handleFaydaCallback();
    }, 300);
  
    return () => clearTimeout(delay);
  }, []);
  
  
  
  
 
  const handleNext = async () => {
    const savedUsername = getSessionItem('exit_username');
  
    setStepLoading(true); // 🔁 start loading
  
    try {
      const recaptchaToken = await recaptchaRef.current.executeAsync();
      recaptchaRef.current.reset();
  
      const res = await axios.post('https://exam.ethernet.edu.et/api/get-record', {
        username: savedUsername,
        recaptcha_token: recaptchaToken,
      });
  
      setStudentProfile(res.data);
      persistDepartmentSession(res.data);

      if (res.data?.is_paid === 1) {
        navigate('/');
        return;
      }

      setActiveStep((prev) => prev + 1);
    } catch (err) {
      console.error('Failed to fetch student profile', err);
      setSnackbar({
        open: true,
        message: 'Something went wrong. Please try again.',
        severity: 'error',
      });
    } finally {
      setStepLoading(false); // 🔁 end loading
    }
  };
  
  
  
  const handleFinish = () => {
    console.log('Finishing... removing session data and navigating');
    // Clean up all session data
    removeSessionItem('exit_username');
    removeSessionItem('fayda_code_verifier');
    removeSessionItem('session_id');
    removeSessionItem('oauth_state');
  
    setTimeout(() => {
      console.log('Clearing profile and navigating now...');
      setStudentProfile(null);
      navigate('/', { replace: true });
    }, 100);
  };
  
  
  const handleBack = () => {
    if (studentProfile?.is_paid === 1) {
      navigate('/'); // redirect to homepage
      return;
    }
    setActiveStep((prev) => prev - 1);
  };

  const handleSaveExamCenter = async () => {
    if (!canSaveExamCenter) return;

    const username = getSessionItem('exit_username');
    if (!username) {
      setExamCenterSaveError({ error: 'Session expired. Please start again from Step 1.' });
      return;
    }

    setSavingExamCenter(true);
    setExamCenterSaveError(null);

    try {
      await axios.post(`${API_BASE}/save-examcenter-KISGHAKNE42243MSSG`, {
        username,
        examcenter_id: selectedCenter.id,
        eap_campus_id: selectedCampus.eap_campus_id,
        exam_center_campus:
          selectedCampus.exam_center_campus || selectedCampus.campus_name,
      });

      const recordRes = await axios.post(`${API_BASE}/get-record`, { username });
      setStudentProfile(recordRes.data);
      setExamCenterSaveSuccess(true);
      setActiveStep(4);
      setSnackbar({
        open: true,
        message: 'Exam center saved successfully. You can now complete payment.',
        severity: 'success',
      });
    } catch (err) {
      const data = err.response?.data;
      const status = err.response?.status;
      if (status === 403 && data?.error) {
        setExamCenterSaveError({ error: data.error, isHtml: true });
      } else if (data?.error) {
        setExamCenterSaveError({ error: data.error });
      } else if (data?.errors) {
        const messages = Object.values(data.errors).flat().join(' ');
        setExamCenterSaveError({ error: messages || 'Validation failed.' });
      } else {
        setExamCenterSaveError({
          error: 'Failed to save exam center. Please try again.',
        });
      }
    } finally {
      setSavingExamCenter(false);
    }
  };
  
  const handleSignIn = () => {;
    setFaydaLoading(true); // show spinner and disable button

    const codeVerifier = uuidv4() + uuidv4();
    const codeChallenge = Base64.stringify(sha256(codeVerifier));
    const STATE = uuidv4(); // Generate state for OAuth security
    
    // Store with session ID to prevent conflicts
    setSessionItem('fayda_code_verifier', codeVerifier);
    setSessionItem('session_id', sessionId); // Store session ID for validation
    setSessionItem('oauth_state', STATE); // Store state to match on callback

      const CLIENT_ID = 'fayda-partner-moe-api';
     const REDIRECT_URI = 'https://exam.ethernet.edu.et/registration';
     const AUTHORIZATION_ENDPOINT = 'https://auth.fayda.et/authorize';

      // const CLIENT_ID = '419258';
      //  const REDIRECT_URI = 'http://localhost:3000/callback';
      //  const AUTHORIZATION_ENDPOINT = 'https://esignet.ltsstaging.fayda.et/authorize';
     const SCOPE = 'openid profile email';
     const ACR_VALUES = 'mosip:idp:acr:generated-code:biometrics'

  
    const claims = {

      userinfo: {
        
        name: { essential: true },
        phone_number: { essential: true },
        email: { essential: true },
        picture: { essential: true },
        gender: { essential: true },
        birthdate: { essential: true },
        address: { essential: true },
      },
    };
  
    const encodedClaims = encodeURIComponent(JSON.stringify(claims));
  
    const authUrl = `${AUTHORIZATION_ENDPOINT}?client_id=${CLIENT_ID}&response_type=code&redirect_uri=${encodeURIComponent(
      REDIRECT_URI
    )}&scope=${encodeURIComponent(SCOPE)}&state=${STATE}&code_challenge=${codeChallenge}&code_challenge_method=S256&acr_values=${ACR_VALUES}&claims=${encodedClaims}`;
  
    // Redirect after short delay
    setTimeout(() => {
      window.location.href = authUrl;
    }, 300); // small delay for UX
  };


 
  
  if (loading) {
    return (
      <ThemeProvider theme={defaultTheme}>
        <div className="page-container">
          <CssBaseline />
          


          <Container component="main" sx={{ maxWidth: 'lg', minHeight: '80vh' }}>
            <Box textAlign="center" sx={{ mt: 10 }}>
              <CircularProgress />
              <Typography variant="h6" sx={{ mt: 3 }}>Fetching your Fayda profile...</Typography>
            </Box>
          </Container>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }
  

  return (

    

    <ThemeProvider theme={defaultTheme}>
             
      <div className="page-container">
        <CssBaseline />

        <ReCAPTCHA
  sitekey="6LepLRQrAAAAAGsabsOe4cjcNnm61WG8fuFvuIUq"
  size="invisible"
  ref={recaptchaRef}
  asyncScriptOnLoad={() => console.log('✅ reCAPTCHA script loaded')}
  onErrored={() => console.error('❌ reCAPTCHA failed to load')}
  onExpired={() => console.warn('⚠️ reCAPTCHA expired')}
/>

        <AppBar position="static">
          <Toolbar sx={{ flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: { xs: 1, sm: 0 } }}>
            <Box
              component="img"
              src={logo}
              alt="Ministry of Education Logo"
              sx={{
                maxHeight: { xs: '50px', sm: '70px' },
                marginRight: { xs: '10px', sm: '20px' },
                width: 'auto'
              }}
            />
            <Typography 
              variant="h6" 
              sx={{ 
                flexGrow: 1,
                fontSize: { xs: '0.9rem', sm: '1.25rem' }
              }}
            >
              Ministry of Education
            </Typography>
          </Toolbar>
        </AppBar>
     
        <Container component="main" sx={{ maxWidth: 'lg', minHeight: '80vh' }}>
          <Box sx={{ mt: 4, mb: 2, textAlign: 'center' }}>
            <Typography component="h1" variant="h5" align="center" sx={{ mb: 0.5, fontWeight: 600, color: '#1252a6' }}>
              Ethiopian University Exit Exam (EUEE) Registration
            </Typography>
            <Divider sx={{ maxWidth: 400, mx: 'auto', my: 1 }} />
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'center',
              alignItems: 'center',
              gap: { xs: 0.5, sm: 1.5 },
              mt: 1
            }}>
              <Typography variant="body2" sx={{ color: '#666' }}>
                📺 Follow Registration guide{' '}
                <Link
                  href={EDUNEWS_VIDEO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontWeight: 600, color: '#1976d2' }}
                >
                  here (Video)
                </Link>
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" sx={{ color: '#666' }}>|</Typography>
                <Link 
                  href="https://t.me/moeexitexamofficial" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontWeight: 600, 
                    color: '#1976d2',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    }
                  }}
                >
                  <TelegramIcon sx={{ fontSize: 18 }} />
                  <Typography component="span" variant="body2">Join Support Group</Typography>
                </Link>
              </Box>
            </Box>
          </Box>

       



            {/* Loading State */}
            {loadingStatus && (
              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="body2" color="textSecondary">
                  Loading registration status...
                </Typography>
              </Box>
            )}

            {/* Error State - Show when there's a network/API error */}
            {!loadingStatus && statusError && (
              <Paper
                elevation={2}
                sx={{
                  mt: 2,
                  p: 3,
                  backgroundColor: '#ffebee',
                  borderLeft: '6px solid #f44336',
                  maxWidth: 700,
                  mx: 'auto',
                  textAlign: 'center',
                  borderRadius: 1,
                }}
              >
                <Typography variant="h6" color="error.main" sx={{ fontWeight: 'bold', mb: 1 }}>
                  ⚠️ Unable to Load Registration Status
                </Typography>
                <Typography variant="body1" sx={{ mt: 1.5, lineHeight: 1.7, color: '#c62828' }}>
                  {statusError}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ mt: 2 }}
                  onClick={async () => {
                    setLoadingStatus(true);
                    setStatusError(null);
                    try {
                      const res = await axios.get('https://exam.ethernet.edu.et/api/registration-status');
                      if (res.data) {
                        const status = res.data.registration_status || res.data.status || 'Closed';
                        setRegistrationStatus(status);  
                        setRegistrationMessage(res.data.registration_message || res.data.message || '');
                        setEnableTicketDownload(res.data.enable_ticket_download || false);
                        setEnableProfileView(res.data.enable_profileview || false);
                        setRegistrationDeadline(res.data.registration_deadline || '');
                      }
                    } catch (error) {
                      console.error('Failed to fetch registration status', error);
                      let errorMessage = 'Failed to fetch registration status. Please check your internet connection and try again.';
                      
                      if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
                        errorMessage = 'Network connection error. Please check your internet connection and try again.';
                      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                        errorMessage = 'Request timed out. Please check your internet connection and try again.';
                      } else if (error.response) {
                        errorMessage = `Unable to load registration status (${error.response.status}). Please try again later.`;
                      } else if (error.message) {
                        errorMessage = error.message;
                      }
                      
                      setStatusError(errorMessage);
                      setRegistrationStatus(null);
                    } finally {
                      setLoadingStatus(false);
                    }
                  }}
                >
                  Retry
                </Button>
              </Paper>
            )}

            {/* Registration Closed Notice - Only show when status is actually Closed from API */}
           {!loadingStatus && !statusError && registrationStatus?.toLowerCase() === 'closed' && (
            <Paper
              elevation={2}
              sx={{
                mt: 2,
                p: 3,
                backgroundColor: '#fff3e0',
                borderLeft: '6px solid #ff9800',
                maxWidth: 700,
                mx: 'auto',
                textAlign: 'center',
                borderRadius: 1,
              }}
            >
              <Typography variant="h6" color="warning.main" sx={{ fontWeight: 'bold', mb: 1 }}>
                🚧 Registration is Currently Closed
              </Typography>
              
              {registrationDeadline && (
                <Box sx={{ mt: 1, mb: 1, p: 0.75, backgroundColor: '#fff', borderRadius: 0.5, display: 'inline-block' }}>
                  <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 500 }}>
                    <CalendarTodayIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    Deadline: <strong>{registrationDeadline}</strong>
                  </Typography>
                </Box>
              )}
              
              <Typography
                variant="body1"
                sx={{ mt: 1.5, lineHeight: 1.7 }}
                dangerouslySetInnerHTML={{ __html: registrationMessage }}
              />
            </Paper>
          )}

{/* Profile Access Link - Only show when enable_profileview is true */}
{enableProfileView && (
  <Paper
    elevation={1}
    sx={{
      mt: 2,
      p: 2,
      backgroundColor: '#f5f5f5',
      maxWidth: 700,
      mx: 'auto',
      textAlign: 'center',
      borderRadius: 1,
    }}
  >
    <Link
      href="/update-preference"
      underline="hover"
      sx={{
        fontSize: '0.95rem',
        fontWeight: 500,
        color: '#1976d2',
        '&:hover': {
          color: '#115293',
          textDecoration: 'underline',
        },
      }}
    >
      🎫 Click here to access your profile and download your entrance ticket
    </Link>
    <Typography variant="body2" sx={{ mt: 1.5, color: '#666' }}>
      Refer to this guide for more information:{' '}
      <Link
        href={EDUNEWS_VIDEO_URL}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ fontWeight: 500, color: '#1976d2' }}
      >
        Watch the guide
      </Link>
    </Typography>
  </Paper>
)}




          {!loadingStatus && !statusError && (registrationStatus?.toLowerCase() === 'open' || registrationStatus?.toLowerCase() === 'opens' || registrationStatus?.toLowerCase() === 'opened') && (
                <>


<Paper
  elevation={2}
  sx={{
    mt: 2,
    mx: 'auto',
    px: 2.5,
    py: 1.5,
    backgroundColor: '#e8f5e9',
    borderLeft: '5px solid #2e7d32',
    maxWidth: 800,
    textAlign: 'center',
    borderRadius: 1,
  }}
>
  <Typography variant="body2" sx={{ color: '#1b5e20', fontWeight: 500 }}>
    This form is for students who took the previous Exit Exam and are registering to retake it.
  </Typography>
  
          {/* Registration Deadline - Show when status is Open */}
          {registrationStatus?.toLowerCase() === 'open' && registrationDeadline && (
            <Box sx={{ mt: 1, py: 0.75, px: 1.5, backgroundColor: '#fff', borderRadius: 1, display: 'inline-block' }}>
              <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarTodayIcon sx={{ fontSize: 16 }} />
                Registration Deadline: <strong>{registrationDeadline}</strong>
              </Typography>
            </Box>
          )}
</Paper>


              
            <Stepper 
              activeStep={activeStep} 
              alternativeLabel 
              sx={{ 
                mt: 5,
                '& .MuiStepLabel-label': {
                  fontSize: { xs: '0.7rem', sm: '0.875rem' }
                }
              }}
            >
            {steps.map((label, index) => (
              <Step key={index}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step 1: Username */}


 
          {activeStep === 0 && (
  <>
    {/* Loader while fetching from backend */}
    {fetchingProfile && (
      <Paper elevation={3} sx={{ mt: 6, p: { xs: 2, sm: 4 }, maxWidth: 600, mx: 'auto', textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Fetching your profile...</Typography>
      </Paper>
    )}

    {/* Show username input only when no profile and not fetching */}
    {!fetchingProfile && !studentProfile && (
      <Paper 
        elevation={3} 
        sx={{ 
          mt: 4, 
          mb: { xs: 12, sm: 10 }, 
          p: { xs: 2, sm: 4 }, 
          maxWidth: 700, 
          mx: 'auto',
          borderRadius: 2,
          border: '1px solid #e0e0e0'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <BadgeIcon sx={{ color: 'primary.main', mr: 1, fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1252a6' }}>
            Step 1: Enter Your Username
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7, color: '#555' }}>
          Your username is the unique ID from your university, used in previous years — such as <strong style={{ color: '#1252a6' }}>EUEE00000123</strong>.  
          It identifies you in the national exam system. Make sure you enter the correct one to proceed.
        </Typography>

        <TextField
          fullWidth
          label="Username"
          variant="outlined"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          sx={{ 
            mb: 2,
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: '#1252a6',
              },
            },
          }}
          placeholder="Enter your username (e.g., EUEE00000123)"
        />

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
            Forgot your username? <Link href="/forgot-username" underline="hover" sx={{ fontWeight: 500 }}>Click here to recover</Link>
          </Typography>
          <Typography variant="body2" color="textSecondary">  
            Completed payment? <Link href="/update-preference" underline="hover" sx={{ fontWeight: 500 }}>Click here to manage your profile</Link> 
          </Typography>
        </Box>
     
        {fetchError && (
          <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
            {fetchError.error}
            {fetchError.link_html && (
              <Box sx={{ mt: 1 }} dangerouslySetInnerHTML={{ __html: fetchError.link_html }} />
            )}
          </Alert>
        )}

        <Box sx={{ mt: 3, textAlign: 'right' }}>
          <Button
            variant="contained"
            size="large"
            disabled={!username || fetchingProfile}
            onClick={async () => {
              setFetchingProfile(true);
              setFetchError('');

              try {
                const res = await axios.post('https://exam.ethernet.edu.et/api/get-record', {
                  username,
                });

                setStudentProfile(res.data);
                persistDepartmentSession(res.data);
                setSessionItem('exit_username', username);
                setSessionItem('session_id', sessionId); // Store session ID for validation
              } catch (err) {
                setFetchError(err.response?.data || { error: 'Failed to fetch record.' });
                setStudentProfile(null);
              } finally {
                setFetchingProfile(false);
              }
            }}
            sx={{
              minWidth: 120,
              py: 1.2,
            }}
          >
            {fetchingProfile ? 'Please wait...' : 'Next'}
          </Button>
        </Box>
      </Paper>
    )}

    {/* Profile Preview Block */}
    {studentProfile && (
      <Paper elevation={3} sx={{ mt: 6, p: { xs: 2, sm: 4 }, maxWidth: 800, mx: 'auto', mb: 8 }}>
        <Typography variant="h6" align="center" gutterBottom>
        🎓 Confirm Your Academic Profile
        </Typography>

        <Divider sx={{ my: 2 }} />

        <List>
      <ListItem>
        <ListItemIcon><AccountCircleIcon color="primary" /></ListItemIcon>
        <ListItemText primary="Full Name" secondary={`${studentProfile.firstname} ${studentProfile.lastname} ${studentProfile.grand_father_name}`} />
      </ListItem>
      <ListItem>
        <ListItemIcon><BadgeIcon color="primary" /></ListItemIcon>
        <ListItemText primary="Username" secondary={studentProfile.username} />
      </ListItem>
      <ListItem>
        <ListItemIcon><WcIcon color="primary" /></ListItemIcon>
        <ListItemText primary="Gender" secondary={studentProfile.gender} />
      </ListItem>
      <ListItem>
        <ListItemIcon><AccountBalanceIcon color="primary" /></ListItemIcon>
        <ListItemText primary="University" secondary={studentProfile.university?.name || 'N/A'} />
      </ListItem>
      <ListItem>
      
        <ListItemIcon><CorporateFareIcon color="primary" /></ListItemIcon>
        <ListItemText primary="Department" secondary={studentProfile.department?.name || 'N/A'} />
      </ListItem>
      <ListItem>
        <ListItemIcon><HomeIcon color="primary" /></ListItemIcon>
        <ListItemText primary="Exam Center" secondary={studentProfile.examcenter?.name || 'N/A'} />
      </ListItem>
      <ListItem>
        <ListItemIcon><PhoneIcon color="primary" /></ListItemIcon>
        <ListItemText primary="Phone Number" secondary={studentProfile.phone_no || 'N/A'} />
      </ListItem>
      <ListItem>
        <ListItemIcon>< AlternateEmailIcon   color="primary" /></ListItemIcon>
        <ListItemText primary="Email" secondary={studentProfile.email || 'N/A'} />
  </ListItem>

          {/* New item for registration status */}
          <ListItem>
            <ListItemIcon><AppRegistrationIcon color="primary" /></ListItemIcon>
            <ListItemText
              primary="Registration Status"
              secondary={studentProfile.is_paid === 1 ? '✅ Completed' : '❌ Incomplete'}
            />
          </ListItem>
        </List>


            {studentProfile.is_paid === 1 ? (
            <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 2,
            backgroundColor: '#e0f2f1',
            border: '1px solid #b2dfdb',
          }}
        >
          
          <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 'bold', mb: 1 }}>
            🎉 Registration Completed
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            You have successfully completed your registration for the upcoming <strong>National Exit Exam</strong>.
            <br />
            {enableProfileView && 'If you wish to update your exam preferences, you can do so by clicking the link below.'}
          </Typography>
          {enableProfileView && (
            <Box sx={{ mt: 2 }}>
              <Link href="update-preference" underline="hover" sx={{ fontWeight: 500 }}>
                ✏️ Update My Preferences
              </Link>
            </Box>
          )}
        </Box>

    ) : (
      <Typography sx={{ mt: 2, color: 'warning.main' }}>
        If this is not your record, click Back. Otherwise, confirm to continue.
      </Typography>
    )}

    <Box sx={{ 
      display: 'flex', 
      flexDirection: { xs: 'column', sm: 'row' },
      justifyContent: { xs: 'stretch', sm: 'space-between' },
      gap: { xs: 2, sm: 2 },
      mt: 3 
    }}>
      <Button 
        variant="contained" 
        color="primary" 
        onClick={() => setStudentProfile(null)}
        fullWidth={{ xs: true, sm: false }}
        sx={{ minWidth: { xs: 'auto', sm: 120 } }}
      >
        Back
      </Button>
     <Button
  variant="contained"
  color="primary"
  disabled={studentProfile.is_paid === 1 || stepLoading}
  onClick={handleNext}
  startIcon={stepLoading ? <CircularProgress color="inherit" size={18} /> : null}
  fullWidth={{ xs: true, sm: false }}
  sx={{ minWidth: { xs: 'auto', sm: 120 } }}
>
  {stepLoading ? 'Loading...' : 'Continue'}
</Button>

    </Box>
  </Paper>
)}

  </>
)}



          {/* Step 2: Fayda Auth */}
          {activeStep === 1 && (
            <>
              <Paper 
                elevation={3} 
                sx={{ 
                  mt: 4, 
                  p: { xs: 2, sm: 4 }, 
                  maxWidth: 700, 
                  mx: 'auto',
                  borderRadius: 2,
                  border: '1px solid #e0e0e0'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <FingerprintIcon sx={{ color: 'primary.main', mr: 1, fontSize: 28 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1252a6' }}>
                    Step 2: Verify Your Fayda ID
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />
                <Box>
                  <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7, color: '#555' }}>
                    To continue with your re-examination registration, please verify your National ID.
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.7, color: '#555' }}>
                    You'll be securely redirected to <strong style={{ color: '#1252a6' }}>Fayda</strong> to confirm your identity. Please click the button below to begin the verification process.
                  </Typography>
                </Box>

                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: { xs: 'stretch', sm: 'space-between' },
                  gap: { xs: 2, sm: 2 },
                  mt: 4 
                }}>
                  <Button 
                    variant="contained" 
                    color="primary"
                    onClick={handleBack}
                    fullWidth={{ xs: true, sm: false }}
                    sx={{ minWidth: { xs: 'auto', sm: 120 } }}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    disabled={faydaLoading}
                    onClick={handleSignIn}
                    startIcon={faydaLoading ? <CircularProgress size={20} /> : null}
                    fullWidth={{ xs: true, sm: false }}
                    sx={{
                      minWidth: { xs: 'auto', sm: 200 },
                      py: 1.2,
                    }}
                  >
                    {faydaLoading ? 'Redirecting...' : 'Authenticate with Fayda'}
                  </Button>
                </Box>
              </Paper>
            </>
          )}

        {/* Step 3: Review Fayda Profile */}
{activeStep === 2 && (
  <>
    <Paper 
      elevation={3} 
      sx={{ 
        mt: 4, 
        p: { xs: 2, sm: 4 }, 
        maxWidth: 800, 
        mx: 'auto',
        mb: 6,
        borderRadius: 2,
        border: '1px solid #e0e0e0'
      }}
    >
      {loading ? (
        <Box textAlign="center">
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>Fetching your Fayda profile...</Typography>
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : savingFayda ? (
        <Box textAlign="center">
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>Saving your profile...</Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <AccountCircleIcon sx={{ color: 'primary.main', mr: 1, fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1252a6' }}>
              Step 3: Review Fayda Profile
            </Typography>
          </Box>
          <Alert severity="warning" sx={{ mb: 3 }}>
            If this is not your profile, please do not continue.
          </Alert>

          <Divider sx={{ mb: 3 }} />

          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            gap: { xs: 3, md: 2 },
            alignItems: { xs: 'center', md: 'flex-start' }
          }}>
            <Box sx={{ width: { xs: '100%', md: '70%' } }}>
              <List>
                <ListItem><ListItemIcon><AccountCircleIcon color="primary" /></ListItemIcon><ListItemText primary="Name" secondary={userInfo.name} /></ListItem>
                <ListItem><ListItemIcon><CalendarTodayIcon color="primary" /></ListItemIcon><ListItemText primary="Birthdate" secondary={userInfo.birthdate} /></ListItem>
                <ListItem><ListItemIcon><WcIcon color="primary" /></ListItemIcon><ListItemText primary="Gender" secondary={userInfo.gender} /></ListItem>

                <ListItem><ListItemIcon><PhoneIcon color="primary"/></ListItemIcon><ListItemText primary="Phone" secondary={userInfo.phone_number
                } /></ListItem>
                 <ListItem><ListItemIcon><HomeIcon color="primary"/></ListItemIcon><ListItemText primary="Address" secondary={`Region: ${address.region}, Zone: ${address.zone}, Woreda: ${address.woreda}, Kebele: ${address.kebele}`} /></ListItem>
              </List>
            </Box>
            {profilePicture && (
  <Box sx={{ width: { xs: '100%', md: '25%' }, display: 'flex', justifyContent: 'center' }}>
    <Box
      component="img"
      src={profilePicture}
      sx={{ width: { xs: 150, md: 120 }, height: { xs: 150, md: 120 }, borderRadius: 2 }}
    />
  </Box>
)}

          </Box>

          <Divider sx={{ my: 3 }} />

          <FormControlLabel
            control={
              <Checkbox
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                color="primary"
              />
            }
            label="I hereby confirm that the above profile matches my educational identity and consent to proceed."
            sx={{ mb: 3 }}
          />
          
          {/* Buttons inside the paper */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: { xs: 'stretch', sm: 'space-between' },
            gap: { xs: 2, sm: 2 },
            mt: 4 
          }}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleBack} 
              disabled={savingFayda}
              fullWidth={{ xs: true, sm: false }}
              sx={{ minWidth: { xs: 'auto', sm: 120 } }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!consentChecked || isSaving}
              onClick={async () => {
                if (isSaving) return; // extra safeguard
                setIsSaving(true);
                try {
                  const username = getSessionItem('exit_username');

                  // Save Fayda profile
                  await axios.post(`${API_BASE}/save-fayda-profile`, {
                    username,
                    sub: userInfo.sub,
                    name: userInfo.name,
                    gender: userInfo.gender,
                    birthdate: userInfo.birthdate,
                    phone_number: userInfo.phone_number || null,
                    email: userInfo.email_address || null,
                    picture: userInfo.picture || null,
                    address: userInfo.address || {},
                  });

                  try {
                    const recordRes = await axios.post(`${API_BASE}/get-record`, { username });
                    setStudentProfile(recordRes.data);
                    persistDepartmentSession(recordRes.data);
                  } catch (recordErr) {
                    console.warn('Could not refresh student record after Fayda save:', recordErr);
                  }

                  setIsSaving(false);
                  setActiveStep(3);
                } catch (err) {
                  alert('Failed to save Fayda profile. Please try again.');
                  setIsSaving(false);
                }
              }}
              startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : null}
              fullWidth={{ xs: true, sm: false }}
              sx={{
                minWidth: { xs: 'auto', sm: 200 },
                py: 1.2,
              }}
            >
              {isSaving ? 'Saving...' : 'This is my profile, continue'}
            </Button>
          </Box>
        </>
      )}
    </Paper>
  </>
)}




{activeStep === 3 && (
  <Paper
    elevation={3}
    sx={{
      mt: 4,
      p: { xs: 2, sm: 4 },
      maxWidth: 700,
      mx: 'auto',
      mb: 6,
      borderRadius: 2,
      border: '1px solid #e0e0e0',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
      <SchoolIcon sx={{ color: 'primary.main', mr: 1, fontSize: 28 }} />
      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1252a6' }}>
        Step 4: Update Your Preference
      </Typography>
    </Box>

    <Typography variant="body1" sx={{ mb: 2, color: '#555' }}>
      Select your exam center and campus. Seat availability is shown for your program
      {examCentersMeta.cohort_year ? ` (${examCentersMeta.cohort_year})` : ''}.
      Campuses marked <strong>Full</strong> cannot be selected.
    </Typography>

    {examCentersMeta.exam_topic && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Program: {examCentersMeta.exam_topic}
      </Typography>
    )}

    <Divider sx={{ mb: 3 }} />

    {loadingCenters ? (
      <Box textAlign="center" sx={{ py: 4 }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading exam centers and seat availability…
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This may take up to a minute. Please do not refresh the page.
        </Typography>
      </Box>
    ) : centersLoadError ? (
      <Alert severity="error" sx={{ mb: 2 }}>
        {centersLoadError}
      </Alert>
    ) : examCentersMeta.exam_centers.length === 0 ? (
      <Alert severity="warning" sx={{ mb: 2 }}>
        No exam centers are available for your program at this time. Please contact support.
      </Alert>
    ) : (
      <>
        <TextField
          select
          fullWidth
          label="Exam Center"
          value={selectedExamCenterId}
          onChange={(e) => {
            setSelectedExamCenterId(e.target.value);
            setSelectedCampusId('');
            setExamCenterSaveError(null);
          }}
          sx={{ mb: 2 }}
        >
          <MenuItem value="">Select exam center</MenuItem>
          {examCentersMeta.exam_centers.map((center) => (
            <MenuItem key={center.id} value={String(center.id)}>
              {center.name}
              {formatCenterRegionSuffix(center.region)}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          fullWidth
          label="Campus"
          value={selectedCampusId}
          onChange={(e) => {
            setSelectedCampusId(e.target.value);
            setExamCenterSaveError(null);
          }}
          disabled={!selectedExamCenterId}
          sx={{ mb: 2 }}
          helperText={
            !selectedExamCenterId
              ? 'Choose an exam center first'
              : 'Select a campus with available seats'
          }
        >
          <MenuItem value="">Select campus</MenuItem>
          {(selectedCenter?.campuses || []).map((campus) => (
            <MenuItem
              key={campus.eap_campus_id}
              value={String(campus.eap_campus_id)}
              disabled={!campusHasSelectableSeats(campus)}
            >
              {formatCampusLabel(campus)}
            </MenuItem>
          ))}
        </TextField>

        {examCenterSaveError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {examCenterSaveError.isHtml ? (
              <Box component="span" dangerouslySetInnerHTML={{ __html: examCenterSaveError.error }} />
            ) : (
              examCenterSaveError.error
            )}
          </Alert>
        )}
      </>
    )}

    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: { xs: 'stretch', sm: 'space-between' },
        gap: { xs: 2, sm: 2 },
        mt: 4,
      }}
    >
      <Button
        variant="contained"
        color="primary"
        onClick={handleBack}
        disabled={savingExamCenter || loadingCenters}
        fullWidth={{ xs: true, sm: false }}
        sx={{ minWidth: { xs: 'auto', sm: 120 } }}
      >
        Back
      </Button>
      <Button
        variant="contained"
        color="primary"
        disabled={!canSaveExamCenter || loadingCenters || !!centersLoadError}
        onClick={handleSaveExamCenter}
        startIcon={savingExamCenter ? <CircularProgress size={18} color="inherit" /> : null}
        fullWidth={{ xs: true, sm: false }}
        sx={{ minWidth: { xs: 'auto', sm: 200 }, py: 1.2 }}
      >
        {savingExamCenter ? 'Saving...' : 'Save & continue to payment'}
      </Button>
    </Box>
  </Paper>
)}

          {/* Step 5: Complete Registration */}
          {activeStep === 4 && examCenterSaveSuccess && (
  <Paper elevation={3} sx={{ mt: 6, mb: 10, p: { xs: 2, sm: 4 }, maxWidth: 700, mx: 'auto' }}>
    <Typography variant="h5" gutterBottom align="center">
      Step 5: Complete Payment and Registration
    </Typography>

    <Divider sx={{ my: 2 }} />

    <Typography variant="body1" sx={{ mb: 3 }} align="center" color="warning.main">
      ⚠️ Your registration is <strong>not complete</strong> until you finish the payment.
    </Typography>

    <Alert severity="warning" sx={{ mb: 3 }}>
      <Typography variant="body2" component="span">
        <strong>Important:</strong> Complete your payment within <strong>24 hours</strong>. If payment is not
        received in time, you may <strong>lose your selected exam center and campus seat</strong>. Seats may
        fill up, and you would then need to <strong>go through registration again</strong> from the beginning
        — including identity verification and selecting a campus again, if places are still available.
      </Typography>
    </Alert>

    <Box
  sx={{
    p: { xs: 2, sm: 3 },
    borderRadius: 2,
    backgroundColor: '#fefefe',
    boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
    mb: 4,
  }}
>
  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
    📄 Payment Summary
  </Typography>

  <Divider sx={{ mb: 2 }} />

  <Box sx={{ 
    display: 'flex', 
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'flex-start', sm: 'center' },
    mb: 1,
    gap: { xs: 0.5, sm: 0 }
  }}>
    <Typography sx={{ width: { xs: '100%', sm: 160 }, mb: { xs: 0.5, sm: 0 } }} color="text.secondary">Payer Name:</Typography>
    <Typography>
      {studentProfile?.firstname} {studentProfile?.lastname} {studentProfile?.grand_father_name}
    </Typography>
  </Box>
  <Box sx={{ 
    display: 'flex', 
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'flex-start', sm: 'center' },
    mb: 1,
    gap: { xs: 0.5, sm: 0 }
  }}>
    <Typography sx={{ width: { xs: '100%', sm: 160 }, mb: { xs: 0.5, sm: 0 } }} color="text.secondary">Payment Method:</Typography>
    <Typography>Telebirr</Typography>
  </Box>

  

  {selectedCenter && selectedCampus && (
    <Box sx={{
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      alignItems: { xs: 'flex-start', sm: 'center' },
      mb: 1,
      gap: { xs: 0.5, sm: 0 },
    }}>
      <Typography sx={{ width: { xs: '100%', sm: 160 }, mb: { xs: 0.5, sm: 0 } }} color="text.secondary">
        Exam Center:
      </Typography>
      <Typography>
        {selectedCenter.name} — {selectedCampus.exam_center_campus || selectedCampus.campus_name}
      </Typography>
    </Box>
  )}

  <Box sx={{ 
    display: 'flex', 
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'flex-start', sm: 'center' },
    mb: 1,
    gap: { xs: 0.5, sm: 0 }
  }}>
    <Typography sx={{ width: { xs: '100%', sm: 160 }, mb: { xs: 0.5, sm: 0 } }} color="text.secondary">Reference Number:</Typography>
    <Typography fontWeight="bold" sx={{ letterSpacing: 1, wordBreak: 'break-word' }}>
      {studentProfile?.reference_number || 'N/A'}
    </Typography>
    </Box>

  <Box sx={{ 
    display: 'flex', 
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'flex-start', sm: 'center' },
    gap: { xs: 0.5, sm: 0 }
  }}>
    <Typography sx={{ width: { xs: '100%', sm: 160 }, mb: { xs: 0.5, sm: 0 } }} color="text.secondary">Service Fee:</Typography>
    <Typography fontWeight="bold" color="success.main">750 ETB</Typography>
  </Box>
</Box>




    <Card variant="outlined" sx={{ mb: 4 }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
 
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: { xs: 1, sm: 0 },
          mb: 2 
        }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            📱 Payment Instructions
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 1, sm: 1.5 }
          }}>
            <Link
              href={EDUNEWS_VIDEO_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontWeight: 500,
                fontSize: '0.9rem',
                color: '#1976d2',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              📺 Watch Payment Guide
            </Link>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, color: '#ccc' }}>|</Box>
            <Link 
              href="https://t.me/moeexitexamofficial" 
              target="_blank" 
              rel="noopener noreferrer" 
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontWeight: 500,
                fontSize: '0.9rem',
                color: '#1976d2',
                '&:hover': {
                  textDecoration: 'underline',
                }
              }}
            >
              <TelegramIcon sx={{ fontSize: 18 }} />
              Join Support Group
            </Link>
          </Box>
        </Box>
        <Box component="ol" sx={{ pl: 3, textAlign: 'left', lineHeight: 2 }}>
          <li>Open the <strong>Telebirr</strong> mobile app</li>
          <li>Tap <strong>Payment</strong></li>
          <li>Select <strong>Education Fee</strong></li>
          <li>Choose <strong>Educational Assessment and Examination Services</strong></li>
          <li>
            Enter the reference number:{' '}
            <strong>{studentProfile?.reference_number || 'N/A'}</strong>
          </li>
          <li>Confirm amount and your details</li>
          <li>Enter your PIN and complete the payment</li>
        </Box>
        <Typography sx={{ mt: 2 }}>
          📩 Once payment is done, you'll receive an SMS. Save the  <strong>transaction number</strong> — you'll need it to update your profile.
        </Typography>

      </CardContent>
    </Card>

    <Box sx={{ 
      display: 'flex', 
      flexDirection: { xs: 'column', sm: 'row' },
      justifyContent: { xs: 'stretch', sm: 'space-between' },
      gap: { xs: 2, sm: 2 },
      mt: 4 
    }}>
  <Button 
    variant="contained" 
    onClick={() => {
      setExamCenterSaveSuccess(false);
      setActiveStep(3);
    }}
    fullWidth={{ xs: true, sm: false }}
    sx={{ minWidth: { xs: 'auto', sm: 120 } }}
  >
    Back
  </Button>

  <Button 
    variant="contained" 
    color="success"  
    onClick={handleFinish}
    fullWidth={{ xs: true, sm: false }}
    sx={{ minWidth: { xs: 'auto', sm: 120 } }}
  >
    Finish
  </Button>
</Box>

  </Paper>
)}
 


          </>
          )}
        </Container>

        <Footer />
     
      </div>
    </ThemeProvider>
  );
}
